import { CalculationMethod } from "containers/Channels";
import type { IChannel } from "containers/Channels/types";
import type { ChannelParams } from "containers/TermFormContainer";
import { useTranslation } from "react-i18next";
import type { CalculationBaseColumn, ICalculationBaseColumnLabel } from "store/calculationBaseColumnLabelsStore";

import CalculationBaseColumnField from "../CalculationBaseColumnField";
import { resolveCalculationBaseColumn } from "../calculationBaseColumnUtils";
import {
    getMilestoneBaseColumnOptions,
    isMilestoneBaseColumnLocked,
    resolveMilestoneBaseColumn,
} from "../milestoneBaseColumnUtils";
import styles from "./style.module.scss";

type ChannelBaseColumnsFieldsProps = {
    channel: Pick<IChannel, "id" | "type" | "calculationMethod" | "hasMilestones">;
    existingChannel: ChannelParams | undefined;
    calculationBaseColumnLabels: ICalculationBaseColumnLabel[];
    onCalculationBaseColumnChange: (channelId: number, calculationBaseColumn: CalculationBaseColumn) => void;
    onMilestoneColumnChange: (channelId: number, milestoneBaseColumn: CalculationBaseColumn) => void;
    danger?: boolean;
};

/** Package (forfait) channels do not use sales-column bases for rights. */
export const channelUsesSalesBaseColumns = (channel: Pick<IChannel, "calculationMethod">): boolean =>
    channel.calculationMethod !== CalculationMethod.PACKAGE;

/**
 * Whether the contract offers a base choice. It is imposed by the calculation method for forfait
 * (turnover) and subscription channels (quantity: rights come from consumption snapshots, not from
 * sales columns), so those only render their rate.
 */
export const channelHasSelectableBaseColumns = (channel: Pick<IChannel, "calculationMethod">): boolean =>
    channelUsesSalesBaseColumns(channel) && channel.calculationMethod !== CalculationMethod.SUBSCRIPTION;

/**
 * Contract-level calculation-base and milestone column selectors for a single channel.
 * - Package / subscription: nothing (base imposed by the calculation method)
 * - No milestones: calculation base only
 * - With milestones: calculation base + milestone base
 */
const ChannelBaseColumnsFields = ({
    channel,
    existingChannel,
    calculationBaseColumnLabels,
    onCalculationBaseColumnChange,
    onMilestoneColumnChange,
    danger = false,
}: ChannelBaseColumnsFieldsProps) => {
    const [t] = useTranslation();

    if (!channelHasSelectableBaseColumns(channel)) return null;

    const showMilestoneBase = !!channel.hasMilestones;
    const baseColumn = resolveCalculationBaseColumn(channel, existingChannel?.calculationBaseColumn);
    const milestoneColumn = resolveMilestoneBaseColumn(existingChannel?.milestoneBaseColumn, baseColumn);
    const milestoneOptions = getMilestoneBaseColumnOptions(baseColumn, calculationBaseColumnLabels, t);
    const isMilestoneLocked = isMilestoneBaseColumnLocked(baseColumn);

    return (
        <div
            className={`${styles["channel-base-columns"]}${
                showMilestoneBase ? "" : ` ${styles["channel-base-columns--single"]}`
            }`}
        >
            <CalculationBaseColumnField
                label={t("ConnectTitle.CalculationBaseColumn")}
                description={t("ConnectTitle.CalculationBaseColumnDescription")}
                value={baseColumn}
                configuredLabels={calculationBaseColumnLabels}
                danger={danger}
                onChange={(calculationBaseColumn) => onCalculationBaseColumnChange(channel.id, calculationBaseColumn)}
            />
            {showMilestoneBase && (
                <CalculationBaseColumnField
                    label={t("ConnectTitle.MilestoneBaseColumn")}
                    description={t("ConnectTitle.MilestoneBaseColumnDescription")}
                    value={milestoneColumn}
                    configuredLabels={calculationBaseColumnLabels}
                    options={milestoneOptions}
                    disabled={isMilestoneLocked}
                    disabledText={isMilestoneLocked ? t("ConnectTitle.MilestoneBaseColumnLockedQuantity") : undefined}
                    danger={danger}
                    onChange={(milestoneBaseColumn) => onMilestoneColumnChange(channel.id, milestoneBaseColumn)}
                />
            )}
        </div>
    );
};

export default ChannelBaseColumnsFields;
